<template>
  <div class="relative" ref="rootRef">
    <button
      @click.stop="open = !open"
      title="Saved views"
      :class="[
        'px-3 py-2 text-sm border rounded-md transition-colors flex items-center gap-1.5',
        'border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark',
        'text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark',
      ]"
    >
      <BookmarkIcon class="h-4 w-4 flex-shrink-0" />
      <span class="hidden md:inline">Views</span>
      <span v-if="savedViews.length" class="hidden md:inline text-xs text-fg-faint dark:text-fg-faint-dark">{{ savedViews.length }}</span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 mt-1 w-56 rounded-lg bg-surface dark:bg-surface-dark shadow-lg ring-1 ring-black/5 dark:ring-stroke-dark z-20"
      @click.stop
    >
      <div class="px-3 py-2 border-b border-stroke-subtle dark:border-stroke-dark">
        <span class="text-xs font-semibold text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider">Saved Views</span>
      </div>

      <div class="py-1 max-h-64 overflow-y-auto">
        <div v-if="!savedViews.length" class="px-3 py-3 text-xs text-fg-faint dark:text-fg-faint-dark text-center">
          No saved views yet
        </div>
        <div
          v-for="view in savedViews"
          :key="view.name"
          class="flex items-center gap-1 px-3 py-1.5 hover:bg-surface-hover dark:hover:bg-surface-hover-dark group"
        >
          <button
            @click="$emit('load', view.name); open = false"
            class="flex-1 text-left text-sm text-fg-secondary dark:text-fg-secondary-dark truncate"
          >{{ view.name }}</button>
          <button
            @click="$emit('remove', view.name)"
            class="opacity-0 group-hover:opacity-100 h-4 w-4 flex items-center justify-center rounded text-fg-faint hover:text-red-500 transition-colors"
            aria-label="Delete view"
          >&times;</button>
        </div>
      </div>

      <div class="border-t border-stroke-subtle dark:border-stroke-dark p-2">
        <div v-if="!saving" class="flex justify-center">
          <button
            @click="saving = true"
            class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >Save current view…</button>
        </div>
        <div v-else class="flex gap-1.5 items-center">
          <input
            ref="nameInput"
            v-model="saveName"
            type="text"
            placeholder="View name…"
            class="flex-1 min-w-0 px-2 py-1 text-xs border border-stroke-strong dark:border-stroke-strong-dark rounded bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark placeholder-fg-muted dark:placeholder-fg-muted-dark"
            @keydown.enter="confirmSave"
            @keydown.esc="saving = false"
          />
          <button
            @click="confirmSave"
            :disabled="!saveName.trim()"
            class="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
          >Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { BookmarkIcon } from '@heroicons/vue/24/outline'

defineProps({
  savedViews: { type: Array, default: () => [] },
})

const emit = defineEmits(['save', 'load', 'remove'])

const open      = ref(false)
const saving    = ref(false)
const saveName  = ref('')
const nameInput = ref(null)
const rootRef   = ref(null)

watch(saving, v => { if (v) nextTick(() => nameInput.value?.focus()) })

const confirmSave = () => {
  if (!saveName.value.trim()) return
  emit('save', saveName.value.trim())
  saving.value = false
  saveName.value = ''
  open.value = false
}

const handleOutsideClick = e => {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false
    saving.value = false
  }
}
onMounted(()  => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))
</script>
